import { adminFetch, buildQueryString } from './admin_api';
import { getAdminToken } from './auth_helper';
import * as Utils from '../Utils';

const reviewsAdminBase = () => `${Utils.API_URL}v1/admin/reviews`;

const authJsonHeaders = (token) => ({
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
});

/**
 * Get all reviews with filtering, pagination and search (Admin only)
 * Endpoint: GET /api/v1/admin/reviews
 * @param {Object} params - Query parameters (page, limit, search, productId, rating, verified, status, startDate, endDate)
 * @param {string} token - Admin authentication token
 * @returns {Promise} API response with reviews list and pagination
 */
export const getAllReviews = (params = {}, token = getAdminToken()) => {
    if (!token) {
        return Promise.resolve({
            success: false,
            message: 'Not Authorized - No Token Provided',
        });
    }

    const queryString = buildQueryString(params);
    return adminFetch(`${reviewsAdminBase()}${queryString}`, {
        method: 'GET',
        headers: authJsonHeaders(token),
    });
};

/**
 * Create a product review (Admin only). Writes to the review collection shown on the storefront.
 * Endpoint: POST /api/v1/admin/reviews
 * @param {Object} payload - { productId, name, rating, comment }
 * @param {string} token - Admin authentication token
 * @returns {Promise} API response
 */
export const createAdminReview = (payload, token = getAdminToken()) => {
    if (!token) {
        return Promise.resolve({
            success: false,
            message: 'Not Authorized - No Token Provided',
        });
    }

    const body = {
        productId: payload.productId,
        name: payload.name,
        rating: Number(payload.rating),
        comment: payload.comment,
    };

    return adminFetch(reviewsAdminBase(), {
        method: 'POST',
        headers: authJsonHeaders(token),
        body: JSON.stringify(body),
    });
};

/**
 * Update review status (Admin only)
 * @param {string} reviewId - Review ID
 * @param {string} status - New status (pending|approved|hidden)
 * @param {string} token - Admin authentication token
 * @returns {Promise} API response
 */
export const updateReviewStatus = (reviewId, status, token = getAdminToken()) => {
    if (!token) {
        return Promise.resolve({
            success: false,
            message: 'Not Authorized - No Token Provided',
        });
    }

    return adminFetch(`${reviewsAdminBase()}/${reviewId}/status`, {
        method: 'PATCH',
        headers: authJsonHeaders(token),
        body: JSON.stringify({ status }),
    });
};

/**
 * Delete a review (Admin only)
 * @param {string} reviewId - Review ID
 * @param {string} token - Admin authentication token
 * @returns {Promise} API response
 */
export const deleteReview = (reviewId, token = getAdminToken()) => {
    if (!token) {
        return Promise.resolve({
            success: false,
            message: 'Not Authorized - No Token Provided',
        });
    }

    return adminFetch(`${reviewsAdminBase()}/${reviewId}`, {
        method: 'DELETE',
        headers: authJsonHeaders(token),
    });
};

/**
 * Parse API response for reviews list
 * @param {Object} res - API response
 * @returns {Object} Normalized response with reviews and pagination
 */
export const parseReviewsResponse = (res) => {
    if (!res) {
        return { reviews: [], pagination: null, error: 'Empty response' };
    }

    if (res.success === false) {
        return { reviews: [], pagination: null, error: res.message };
    }

    let reviews = [];
    if (Array.isArray(res?.data?.reviews)) {
        reviews = res.data.reviews;
    } else if (Array.isArray(res?.reviews)) {
        reviews = res.reviews;
    } else if (Array.isArray(res?.data)) {
        reviews = res.data;
    } else if (Array.isArray(res)) {
        reviews = res;
    }

    const pagination = res?.data?.pagination || res?.pagination || null;

    return { reviews, pagination, error: null };
};

/**
 * Label for admin product pickers (name + sizes, never SKU).
 * @param {Object} product
 * @returns {string}
 */
export const formatProductPickerLabel = (product) => {
    if (!product) return '—';
    const name = product.name || product.title || 'Unnamed product';
    const sizes = Array.isArray(product.sizes)
        ? product.sizes.filter(Boolean).join(', ')
        : '';
    return sizes ? `${name} (${sizes})` : name;
};
